import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, ElementRef, Renderer2, AfterViewInit } from '@angular/core';
import { CmsService, CMSTabParagraphContainer, I18nModule, ProductReference, ProductReferenceService, UrlModule, WindowRef } from '@spartacus/core';
import { Observable, combineLatest, of } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { CmsComponentData, CurrentProductService, PageComponentModule, PageLayoutService, TabParagraphContainerModule } from "@spartacus/storefront";
import { GenTabParagraphContainerComponent } from '../../tab-paragraph-container/tab-paragraph-container.component';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { GenProductTabReference } from 'src/app/generac/models/product.model';
import { GenProductService } from 'src/app/generac/services/product.service';
import { GenPdpTabs } from 'src/app/generac/enums/pdp-tabs.enum';
import { CommonModule } from '@angular/common';
import { GenDocumentService } from 'src/app/generac/services/document.service';

@Component({
    selector: 'gen-product-header-menu',
    templateUrl: './product-header-menu.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
      CommonModule,
      RouterModule,
      UrlModule,
      I18nModule,
      PageComponentModule,
      TabParagraphContainerModule,
    ],
})
export class GenProductHeaderMenuComponent extends GenTabParagraphContainerComponent implements AfterViewInit {
  section = 'ProductDetailsPageTemplate';
  slots: any[];
  scrollByClick: boolean = false;

  slots$: Observable<any | undefined[]> = this.currentProductService.getProduct().pipe(
    switchMap((product) => combineLatest([
      of(product),
      this.pageLayoutService.getSlots(this.section),
      this.isReferencesExist(product.code, 'SIMILAR'),
      this.isTabReferencesExist(product.code, 'ACCESSORIES')
    ])),
    map(([product, slots, isSimilarExist, isAccessoriesExist]) => {
      return slots.map((slot: string) => {
        const slotName = slot as keyof typeof GenPdpTabs;
        if (!GenPdpTabs[slotName]) {
          return null;
        }
        return {
          title: GenPdpTabs[slotName],
          anchor: GenPdpTabs[slotName].replace(/\s/g, ''),
          className: slotName,
          hidden: !this.isSlotShown(slotName, product, isSimilarExist, isAccessoriesExist)
        }
      })
    }),
    tap(slots => this.slots = slots),
    tap(() => this.setTabActive('Summary'))
  );

  @HostListener('window:scroll', ['$event'])
  onScroll(event: Event) {
    if (event && !this.scrollByClick) {
      this.observeTabPostition();
    }
  }

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    public override componentData: CmsComponentData<CMSTabParagraphContainer>,
    protected override cmsService: CmsService,
    protected override winRef: WindowRef,
    protected pageLayoutService: PageLayoutService,
    protected override currentProductService: CurrentProductService,
    protected productReferenceService: ProductReferenceService,
    protected override productService: GenProductService,
    private router: Router,
    private activateRoute: ActivatedRoute,
    cdr: ChangeDetectorRef,
    documentService: GenDocumentService,
  ) {
    super(componentData, cmsService, winRef, currentProductService, productService, activateRoute, cdr, documentService);
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    
    if (this.winRef.nativeWindow?.innerWidth <= 1200) return;

    const header = document.querySelector('#cx-header') as HTMLElement;
    if (!header) return;

    const menu = this.elementRef.nativeElement.parentElement;
    const updateTop = () =>
      this.renderer.setStyle(
        menu,
        'top',
        `${header.getBoundingClientRect().height}px`
      );

    updateTop();
    new ResizeObserver(updateTop).observe(header);
  }

  private observeTabPostition(): void {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && ((entry.intersectionRatio > 0.3 &&
          entry.boundingClientRect.height / entry.rootBounds.height > 1.2)) || entry.intersectionRatio > 0.95) {
          const tab = entry.target.classList[0];
          if (this.activeTabNum !== this.getIndexOfTab(tab)) {
              this.setTabActive(tab);
          }
        }
      });
    });
    this.slots?.forEach((slot) => {
      if (slot && !slot.hidden) {
        const elementToTrack = document.getElementsByClassName(slot.className)[0] as HTMLElement;
        if (elementToTrack) {
          observer.observe(elementToTrack);
        }
      }
    })
  }

  private setTabActive(tabName: string): void {
    this.activeTabNum = this.getIndexOfTab(tabName);
    this.router.navigate([], {
      fragment: this.slots[this.activeTabNum].anchor,
      replaceUrl: true,
      queryParamsHandling: 'merge',
    });
    if(this.scrollByClick) {
      setTimeout(()=> {
        this.scrollByClick = false;
      }, 500);
    }
  }

  private getIndexOfTab(tabName: string): number {
    return this.slots.findIndex((slot) => slot?.className == tabName);
  }

  private isReferencesExist(code: string, referenceType: string): Observable<boolean> {
    return this.productReferenceService
      .getProductReferences(code, referenceType)
      .pipe(
        filter((references) => Boolean(references)),
        map((references: ProductReference[]) => {
          return references.map((reference) => of(reference.target));
        }),
        switchMap((products) => {
          return of(products.length > 0 ? true : false)
        })
      );
  }

  private isTabReferencesExist(code: string, referenceType: string): Observable<boolean> {
    return this.productService
      .getProductTabReferences(code, referenceType)
      .pipe(
        filter((references) => Boolean(references)),
        switchMap((tabReferences: GenProductTabReference[]) => {
          return of(tabReferences.some((tabReference)=> tabReference.products.length > 0))
        })
      );
  }

  public scrollToSlot(slotName: string): void {
    this.scrollByClick = true;
    const target = document.getElementsByClassName(slotName)[0] as HTMLElement;

    const header = document.querySelector('#cx-header') as HTMLElement;
    const menu = this.elementRef.nativeElement.parentElement as HTMLElement;
    const totalOffset = (header?.getBoundingClientRect().height ?? 0) + (menu?.getBoundingClientRect().height ?? 0);
    const targetPosition = target.getBoundingClientRect().top + window.scrollY - totalOffset;

    if (target) {
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
      this.setTabActive(slotName);
    }
  }

  public isSlotShown(slotName: string, product: any, isSimilarExist: boolean, isAccessoriesExist: boolean): boolean {
    if ((slotName == 'Bundles' && !product.bundleAvailable) || (slotName == 'UpSelling' && !isAccessoriesExist) ||
      (slotName == 'CrossSelling' && !isSimilarExist)) {
      return false;
    }
    return true;
  }
}
